'use client'
import React, { useState, useEffect } from "react"
import { useWatch } from "react-hook-form";
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../@/components/ui/select";
import { Clock, Minus, Plus } from "lucide-react"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { ClipLoader } from "react-spinners";
import { useAuth } from "@clerk/clerk-react"
import { Calendar } from "../../@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../../@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils"
import { addDays, addWeeks, addMonths } from "date-fns";

// Modern Duration Picker Component (same as Edit Cohort)
function DurationPicker({ value, unit, onValueChange, onUnitChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [tempUnit, setTempUnit] = useState(unit);

  // Keep field-like controls from changing border color on hover.
  const lockNeutralBorderOnHover = "hover:border-(--neutral-300)";

  const valueNum = Number(value);
  const hasDuration = Number.isFinite(valueNum) && valueNum > 0 && Boolean(unit);

  // Format display text
  const displayText = hasDuration ? `${valueNum} ${unit}` : "Set duration";

  // Quick preset options
  const presets = [
    { label: "1 Week", value: 1, unit: "weeks" },
    { label: "2 Weeks", value: 2, unit: "weeks" },
    { label: "1 Month", value: 1, unit: "months" },
    { label: "3 Months", value: 3, unit: "months" },
  ];

  const handlePresetClick = (preset) => {
    onValueChange(preset.value);
    onUnitChange(preset.unit);
    setTempValue(preset.value);
    setTempUnit(preset.unit);
    setIsOpen(false);
  };

  const handleApply = () => {
    onValueChange(tempValue);
    onUnitChange(tempUnit);
    setIsOpen(false);
  };

  const increment = () => {
    setTempValue((prev) => Math.min(Number(prev) + 1, 999));
  };

  const decrement = () => {
    setTempValue((prev) => Math.max(Number(prev) - 1, 1));
  };

  useEffect(() => {
    setTempValue(hasDuration ? valueNum : 1);
    setTempUnit(unit || "days");
  }, [value, unit, isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal bg-(--surface-base) border-2 border-(--neutral-300)",
            lockNeutralBorderOnHover
          )}
        >
          <span
            className={cn(
              "flex items-center gap-2",
              hasDuration ? "text-(--neutral-700)" : "text-muted-foreground"
            )}
          >
            <Clock className="w-4 h-4 text-(--neutral-500)" />
            {displayText}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="space-y-1">
            <h4 className="font-medium text-sm">Set Duration</h4>
            <p className="text-xs text-(--neutral-500)">Choose a preset or customize</p>
          </div>

          {/* Quick Presets */}
          <div className="grid grid-cols-2 gap-2">
            {presets.map((preset) => {
              const isSelected = valueNum === preset.value && unit === preset.unit;
              return (
                <Button
                  key={`${preset.value}-${preset.unit}`}
                  type="button"
                  variant="outline"
                  onClick={() => handlePresetClick(preset)}
                  className={cn(
                    "h-9 text-sm border-2 border-(--neutral-300)",
                    isSelected
                      ? "border-(--color-primary) text-(--color-primary) hover:border-(--color-primary)"
                      : lockNeutralBorderOnHover
                  )}
                >
                  {preset.label}
                </Button>
              );
            })}
          </div>

          {/* Custom Duration */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-(--neutral-700)">Custom Duration</div>

            {/* Stepper and Value */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 flex-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-9 w-9 shrink-0 border-2 border-(--neutral-300)",
                    lockNeutralBorderOnHover
                  )}
                  onClick={decrement}
                  disabled={Number(tempValue) <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>

                <Input
                  type="number"
                  min={1}
                  max={999}
                  value={tempValue}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setTempValue(Math.max(1, Math.min(999, val)));
                  }}
                  className="text-center h-9 border-(--neutral-300)"
                />

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-9 w-9 shrink-0 border-2 border-(--neutral-300)",
                    lockNeutralBorderOnHover
                  )}
                  onClick={increment}
                  disabled={Number(tempValue) >= 999}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Unit Selector */}
              <Select value={tempUnit} onValueChange={setTempUnit}>
                <SelectTrigger className="w-27.5 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                  <SelectItem value="months">Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Apply Button */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              className={cn("flex-1 border-2 border-(--neutral-300)")}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleApply} className={cn("flex-1")}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const addCohortSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["full-time", "part-time"], { errorMap: () => ({ message: "Type is required" }) }),
  duration_value: z.coerce.number().min(1, "Duration is required"),
  duration_unit: z.enum(["days", "weeks", "months"], { errorMap: () => ({ message: "Unit is required" }) }),
  start_date: z.date({ required_error: "Start date is required" }),
  end_date: z.date().optional()
}).refine(
  (data) => !data.end_date || data.start_date <= data.end_date,
  { message: "End date must be after start date", path: ["end_date"] }
)

export function AddCohorts({ onAdd, refetchCohorts }) {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { getToken } = useAuth()

  function toDateInputValue(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    // Get YYYY-MM-DD
    return date.toISOString().slice(0, 10);
  }

  const form = useForm({
    resolver: zodResolver(addCohortSchema),
    defaultValues: {
      name: '',
      type: 'full-time',
      duration_value: 7,
      duration_unit: "days",
      start_date: undefined,
      end_date: undefined
    }
  })

  const handleCancel = () => {
    form.reset()
    setOpen(false)
  }

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true)

      const token = await getToken()
      if (!token) return

      const payload = {
        name: values.name,
        type: values.type,
        duration_value: values.duration_value,
        duration_unit: values.duration_unit,
        start_date: values.start_date ? format(values.start_date, "yyyy-MM-dd") : null,
        end_date: values.end_date ? format(values.end_date, "yyyy-MM-dd") : null
      }
      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      }

      console.log("Submitting:", payload)
      const response = await axios.post(api_endpoint + "/cohort/create", payload, reqConfig)
      console.log('Cohort created:', response.data)
      toast.success(`Added cohort "${values.name}" successfully!`)
      if (onAdd) onAdd(response.data)
      if (refetchCohorts) refetchCohorts() // refetch cohorts
      form.reset()
      setOpen(false)

    } catch (error) {
      console.error("Failed to create cohort:", error)
      toast.error("Failed to create cohort. See console.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const startDate = useWatch({ control: form.control, name: "start_date" });
  const durationValue = useWatch({ control: form.control, name: "duration_value" });
  const durationUnit = useWatch({ control: form.control, name: "duration_unit" });

  useEffect(() => {

    if (startDate instanceof Date && !isNaN(startDate) && durationValue && durationUnit) {
      let endDate;
      const durationNum = Number(durationValue)
      switch (durationUnit) {
        case "days":
          endDate = addDays(startDate, durationNum);
          break;
        case "weeks":
          endDate = addWeeks(startDate, durationNum);
          break;
        case "months":
          endDate = addMonths(startDate, durationNum);
          break;
        default:
          endDate = null;
      }
      if (endDate) {
        form.setValue("end_date", endDate);
      }
    } else {
      form.setValue("end_date", undefined);
    }
  }, [startDate, durationValue, durationUnit, form]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant=""
          className="w-full md:w-auto flex items-center justify-center gap-2"
        >
          Add Cohort
          <Plus className="w-4 h-4" />
        </Button>
      </DialogTrigger>

      <DialogContent className="h-auto text-(--neutral-900) font-body">
        <DialogHeader className="px-4 py-3 border-(--neutral-200)">
          <DialogTitle className="font-heading text-(--neutral-900)">Add new cohort</DialogTitle>
          <DialogDescription className="text-(--neutral-800)">Create a cohort and we’ll calculate the end date automatically.</DialogDescription>
        </DialogHeader>

        <div className="px-1 sm:px-2 md:px-4 pt-4 pb-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="add-cohort-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading text-(--neutral-900)">Cohort name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter cohort name"
                          className="font-body border border-(--neutral-300) bg-(--surface-base) text-(--neutral-900) placeholder:text-(--neutral-500) focus-visible:ring-1"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  className="w-full"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading text-(--neutral-800)">Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl className="w-full">
                          <SelectTrigger className="h-11 text-(--neutral-900) border border-(--neutral-300)">
                            <SelectValue placeholder="Select cohort type" className="font-body" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-(--surface-base) text-(--neutral-900)">
                          <SelectItem value="full-time" className="font-body hover:bg-(--neutral-100)">Full-time</SelectItem>
                          <SelectItem value="part-time" className="font-body hover:bg-(--neutral-100)">Part-time</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* MODERN DURATION PICKER - Single click opens popover */}
              <FormField
                control={form.control}
                name="duration_value"
                render={({ field: valueField }) => (
                  <FormField
                    control={form.control}
                    name="duration_unit"
                    render={({ field: unitField }) => (
                      <FormItem>
                        <FormLabel className="font-heading text-(--neutral-800)">Duration *</FormLabel>
                        <FormControl>
                          <DurationPicker
                            value={valueField.value}
                            unit={unitField.value}
                            onValueChange={(val) => valueField.onChange(val)}
                            onUnitChange={(nextUnit) => unitField.onChange(nextUnit)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading text-(--neutral-800)">Start date *</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal border border-(--neutral-300) bg-(--surface-base) text-(--neutral-900)",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              form.getValues("end_date") && date > form.getValues("end_date")
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading text-(--neutral-800)">End date (optional)</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal border border-(--neutral-300) bg-(--surface-base) text-(--neutral-900) hover:bg-(--neutral-100)",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 border border-(--neutral-200) bg-(--surface-panel)" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              form.getValues("start_date") && date < form.getValues("start_date")
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </Form>
        </div>

        <DialogFooter className="">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
              className=""
            >
              Cancel
            </Button>
          </DialogClose>

          <Button
            variant=""
            type="submit"
            form="add-cohort-form"
            className=""
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#ffffff" />
              </span>
            ) : (
              "Add Cohort"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddCohorts
